import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getClientMe, loginClient } from '../api/clientPortal';

const STORAGE_KEY = 'studioSivadClientAuth';
const BRAND_STORAGE_KEY = 'studioSivadClientBrandId';

const ClientAuthContext = createContext({
  clientUser: null,
  clientBrand: null,
  clientBrands: [],
  token: null,
  loading: true,
  login: async () => {},
  logout: () => {},
  selectClientBrand: () => {},
  applySessionUpdate: () => {},
});

export const ClientAuthProvider = ({ children }) => {
  const [clientUser, setClientUser] = useState(null);
  const [clientBrand, setClientBrand] = useState(null);
  const [clientBrands, setClientBrands] = useState([]);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setClientUser(null);
    setClientBrand(null);
    setClientBrands([]);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(BRAND_STORAGE_KEY);
  }, []);

  const applySession = useCallback((data) => {
    const brands = Array.isArray(data?.brands) ? data.brands : (data?.brand ? [data.brand] : []);
    const storedBrandId = localStorage.getItem(BRAND_STORAGE_KEY);
    const selected =
      brands.find((brand) => String(brand?.id || brand?._id || '') === String(storedBrandId || '')) ||
      brands.find((brand) => brand?.selected) ||
      data?.brand ||
      brands[0] ||
      null;
    const selectedId = selected?.id || selected?._id || '';
    if (selectedId) localStorage.setItem(BRAND_STORAGE_KEY, String(selectedId));
    setClientUser(data?.user || null);
    setClientBrands(brands);
    setClientBrand(selected);
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await loginClient(email, password);
    if (!data?.token) {
      throw new Error('Login did not return a token.');
    }

    localStorage.setItem(STORAGE_KEY, data.token);
    setToken(data.token);
    applySession(data);
    return data;
  }, [applySession]);

  const selectClientBrand = useCallback((brandId) => {
    const selected = clientBrands.find((brand) => String(brand?.id || brand?._id || '') === String(brandId || ''));
    if (!selected) return false;
    localStorage.setItem(BRAND_STORAGE_KEY, String(selected.id || selected._id));
    setClientBrand(selected);
    return true;
  }, [clientBrands]);

  useEffect(() => {
    let active = true;

    const init = async () => {
      try {
        const storedToken = localStorage.getItem(STORAGE_KEY);
        if (!storedToken) {
          if (active) {
            setLoading(false);
          }
          return;
        }

        setToken(storedToken);
        const me = await getClientMe();

        if (!active) {
          return;
        }

        applySession(me);
      } catch (error) {
        if (active) {
          logout();
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      active = false;
    };
  }, [logout, applySession]);

  const value = useMemo(
    () => ({
      clientUser,
      clientBrand,
      clientBrands,
      token,
      loading,
      login,
      logout,
      selectClientBrand,
      applySessionUpdate: applySession,
    }),
    [clientUser, clientBrand, clientBrands, token, loading, login, logout, selectClientBrand, applySession]
  );

  return <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>;
};

export const useClientAuth = () => useContext(ClientAuthContext);
